import { AlertCircle, ChevronDown, X } from "lucide-react";
import { useId, useState, type FC } from "react";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import type { ComposerAttachment } from "../types";
import {
  attachmentTypeLabel,
  attachmentTypeMix,
  formatBytes,
  groupAttachmentsByStem,
  type ComposerAttachmentGroup
} from "../utils";

const isFailed = (i: ComposerAttachment) => i.status === "failed";
const isUploading = (i: ComposerAttachment) => i.status === "uploading";

const ItemIcon: FC<{ item: ComposerAttachment }> = ({ item }) => {
  if (isUploading(item))
    return <Spinner aria-hidden className="size-3.5 shrink-0 text-fg-3" />;
  if (isFailed(item))
    return <AlertCircle className="size-3.5 shrink-0 text-danger" />;
  return (
    <FileTypeIcon name={item.filename} mimeType={item.contentType} size={14} />
  );
};

/** The file whose state the row shows: a failure, then an upload, then the first. */
const leadOf = (group: ComposerAttachmentGroup): ComposerAttachment =>
  group.items.find(isFailed) ?? group.items.find(isUploading) ?? group.items[0];

const groupMeta = (group: ComposerAttachmentGroup): string => {
  if (group.items.some(isFailed)) return "Upload failed";
  const uploading = group.items.filter(isUploading);
  if (uploading.length) {
    const progress =
      uploading.reduce((s, i) => s + i.progress, 0) / uploading.length;
    return `Uploading ${Math.round(progress)}%`;
  }
  return formatBytes(group.items.reduce((s, i) => s + i.size, 0));
};

const TrayRow: FC<{
  group: ComposerAttachmentGroup;
  onRemove: (localId: string) => void;
}> = ({ group, onRemove }) => {
  const paired = group.items.length > 1;
  const lead = leadOf(group);
  const failed = group.items.some(isFailed);
  const label = paired ? group.name : group.items[0].filename;
  const title = group.items
    .map((i) => (i.error ? `${i.filename}: ${i.error}` : i.filename))
    .join("\n");
  return (
    <li
      className="group/row flex h-8 min-w-0 items-center gap-2 pr-1 pl-2.5 hover:bg-fill-1"
      data-testid="composer-attachment-row"
      data-status={lead.status}
      title={title}
    >
      <ItemIcon item={lead} />
      <span className="min-w-0 flex-1 truncate text-base text-fg">{label}</span>
      {paired
        ? group.items.map((item) => (
            <Tag
              key={item.localId}
              className={cn("pr-0.5", isFailed(item) && "text-danger")}
              data-testid="composer-attachment-tag"
            >
              {attachmentTypeLabel(item.filename, item.contentType)}
              <button
                type="button"
                aria-label={`Remove ${item.filename}`}
                className="inline-flex size-4 items-center justify-center rounded-xs text-fg-3 hover:bg-fill-3 hover:text-fg"
                onClick={() => onRemove(item.localId)}
              >
                <X />
              </button>
            </Tag>
          ))
        : null}
      <span
        className={cn("shrink-0 text-sm", failed ? "text-danger" : "text-fg-3")}
      >
        {groupMeta(group)}
      </span>
      <Button
        variant="ghost"
        size="icon-2xs"
        aria-label={`Remove ${label}`}
        data-testid="composer-attachment-remove"
        className="opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
        onClick={() => group.items.forEach((i) => onRemove(i.localId))}
      >
        <X />
      </Button>
    </li>
  );
};

/**
 * Many files in the composer, as one summary bar (count, type mix, size,
 * upload state) that opens into a list. Files that differ only by extension
 * share a row; failed uploads are listed first.
 */
export const ComposerAttachmentTray: FC<{
  items: ComposerAttachment[];
  onRemove: (localId: string) => void;
  onRemoveAll: () => void;
}> = ({ items, onRemove, onRemoveAll }) => {
  const [isOpen, setIsOpen] = useState(false);
  const listId = useId();
  const groups = groupAttachmentsByStem(items);
  const uploading = items.filter(isUploading).length;
  const failed = items.filter(isFailed).length;
  const totalBytes = items.reduce((s, i) => s + i.size, 0);

  return (
    <div
      className={cn(
        "w-full overflow-hidden rounded-lg bg-fill-1",
        failed > 0 && !isOpen && "ring-1 ring-danger-line"
      )}
      data-testid="composer-attachment-tray"
    >
      <div className="flex h-9 items-center gap-1 pr-1.5">
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={listId}
          onClick={() => setIsOpen((open) => !open)}
          className="flex h-full min-w-0 flex-1 items-center gap-2 rounded-lg pl-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          data-testid="composer-attachment-tray-toggle"
        >
          <span className="flex shrink-0 items-center gap-0.5">
            {items.slice(0, 3).map((item) => (
              <FileTypeIcon
                key={item.localId}
                name={item.filename}
                mimeType={item.contentType}
                size={14}
              />
            ))}
          </span>
          <span className="shrink-0 text-base font-medium text-fg">
            {`${items.length} Files`}
          </span>
          <span className="min-w-0 truncate text-sm text-fg-3">
            {`${attachmentTypeMix(items)} · ${formatBytes(totalBytes)}`}
          </span>
          {uploading > 0 ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-sm text-fg-3">
              <Spinner aria-hidden className="size-3" />
              {`Uploading ${uploading}`}
            </span>
          ) : null}
          {failed > 0 ? (
            <span className="shrink-0 text-sm text-danger">{`${failed} Failed`}</span>
          ) : null}
          <ChevronDown
            aria-hidden
            className={cn(
              "ml-auto size-3.5 shrink-0 text-fg-3 transition-transform",
              isOpen && "rotate-180"
            )}
          />
        </button>
        <Button
          variant="ghost"
          size="xs"
          onClick={onRemoveAll}
          data-testid="composer-attachment-clear"
        >
          Clear All
        </Button>
      </div>
      {isOpen ? (
        <ul
          id={listId}
          className="max-h-49 divide-y divide-line-1 overflow-y-auto border-t border-line-1 bg-elevated"
        >
          {groups.map((group) => (
            <TrayRow key={group.key} group={group} onRemove={onRemove} />
          ))}
        </ul>
      ) : null}
    </div>
  );
};
